import React, { useState } from 'react';
import { X, Calendar, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';

interface RiskProspect {
  id: string;
  first_name: string;
  last_name: string;
  follow_up_date?: string;
  // Other properties not needed for this component
  [key: string]: any;
}

interface ScheduleFollowUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  prospect: RiskProspect;
  onSchedule: (date: string) => void;
}

const ScheduleFollowUpModal: React.FC<ScheduleFollowUpModalProps> = ({ 
  isOpen, 
  onClose, 
  prospect, 
  onSchedule 
}) => {
  const today = new Date().toISOString().split('T')[0];
  const [followUpDate, setFollowUpDate] = useState(today);
  const [followUpTime, setFollowUpTime] = useState('09:00');
  const [followUpType, setFollowUpType] = useState('call');
  const [followUpNotes, setFollowUpNotes] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Combine date and time
    const dateTime = new Date(`${followUpDate}T${followUpTime}`);
    onSchedule(dateTime.toISOString());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Schedule Follow-Up</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600">
            Scheduling follow-up for <span className="font-medium text-gray-900">{prospect.first_name} {prospect.last_name}</span>
          </p>
        </div>
        
        {prospect.follow_up_date && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Existing Follow-Up</p>
                <p className="text-sm text-yellow-700">
                  This prospect already has a follow-up scheduled for {new Date(prospect.follow_up_date).toLocaleDateString()} at {new Date(prospect.follow_up_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="followUpDate" className="block text-sm font-medium text-gray-700 mb-1">
                Follow-Up Date
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  id="followUpDate"
                  min={today}
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  required
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="followUpTime" className="block text-sm font-medium text-gray-700 mb-1">
                Follow-Up Time
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="time"
                  id="followUpTime"
                  value={followUpTime}
                  onChange={(e) => setFollowUpTime(e.target.value)}
                  className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  required
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="followUpType" className="block text-sm font-medium text-gray-700 mb-1">
                Follow-Up Type
              </label>
              <select
                id="followUpType"
                value={followUpType}
                onChange={(e) => setFollowUpType(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="call">Phone Call</option>
                <option value="email">Email</option>
                <option value="meeting">Meeting</option>
                <option value="video">Video Call</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="followUpNotes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                id="followUpNotes"
                rows={3}
                value={followUpNotes}
                onChange={(e) => setFollowUpNotes(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Add any notes about this follow-up..."
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
            >
              Schedule Follow-Up
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleFollowUpModal;